import { useState } from "react";
import { Sparkles, TrendingUp, Boxes, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const t = line.trim();
    const k = `ln-${i}-${t.slice(0, 16)}`;
    if (!t) return <div key={k} className="h-2" />;
    if (/^#{1,3}\s/.test(t)) return <h4 key={k} className="font-heading text-xl font-semibold mt-4 mb-1">{t.replace(/^#+\s/, "")}</h4>;
    if (/^(\*\*|__).+(\*\*|__):?$/.test(t)) return <p key={k} className="font-semibold mt-3">{t.replace(/[*_]/g, "")}</p>;
    if (/^[-*•]\s/.test(t)) return <li key={k} className="ml-5 list-disc text-foreground/80">{t.replace(/^[-*•]\s/, "").replace(/\*\*/g, "")}</li>;
    return <p key={k} className="text-foreground/80">{t.replace(/\*\*/g, "")}</p>;
  });
}

function ForecastCard({ icon: Icon, title, desc, endpoint, testid, accent }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, { horizon_days: 7 });
      setResult(data.forecast);
    } catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <span className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </span>
        <div className="flex-1">
          <h3 className="text-lg font-medium">{title}</h3>
          <p className="text-muted-foreground text-sm mt-0.5">{desc}</p>
        </div>
      </div>
      <Button data-testid={`${testid}-btn`} onClick={run} disabled={loading}
        className="mt-5 bg-primary hover:bg-[#B35A42] text-primary-foreground">
        {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate forecast</>}
      </Button>
      {result && (
        <div data-testid={`${testid}-result`} className="mt-5 pt-5 border-t border-border text-sm leading-relaxed animate-fade-up">
          {renderText(result)}
        </div>
      )}
    </div>
  );
}

export default function Forecasting() {
  return (
    <div data-testid="forecasting-page">
      <PageHeader
        title="AI Forecasting"
        subtitle="Predict sales and inventory needs powered by AI."
      />
      <div className="bg-accent/30 border border-accent rounded-xl p-4 mb-6 flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-primary" />
        <p className="text-sm text-foreground/80">Forecasts analyze your real sales and inventory history. Add more daily records for sharper predictions.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <ForecastCard icon={TrendingUp} title="Sales Forecast" desc="Project next 7 days of revenue with trends & recommendations."
          endpoint="/ai/sales-forecast" testid="sales-forecast" accent="bg-primary/12 text-primary" />
        <ForecastCard icon={Boxes} title="Inventory Forecast" desc="Predict stock-outs and get reorder recommendations."
          endpoint="/ai/inventory-forecast" testid="inventory-forecast" accent="bg-secondary/15 text-secondary" />
      </div>
    </div>
  );
}
